import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  bootstrapOrg,
  listMyMemberships,
  listMyOrganizations,
  listMyArchivedOrganizations,
  listStoresForOrg,
  membershipIsOrgWide,
  ORG_ARCHIVE_RESTORE_WINDOW_DAYS,
  orgRestoreDeadline,
  unarchiveMyOrganization,
  type Membership,
  type Organization,
  type Store,
} from '@pc/db';
import { slugify, describeError } from '@pc/core';
import { BootLoader, Button, Card, Display, Field, Input, Rule, SmallCap } from '@pc/ui';
import { useAuth } from './AuthProvider';
import { useActiveContext } from './ActiveContextProvider';

// After committing the new active context, leave the SPA via a full
// page load instead of `useNavigate('/')`. AppRouter rebuilds
// `createBrowserRouter` via `useMemo` keyed on `mode` (see
// `apps/web/src/App.tsx`), so when `setActiveOrg` / `setActiveOrgMode`
// flips `mode` mid-handler the `<RouterProvider>` swaps router
// instances. The captured `nav` reference is then bound to the OLD
// router; calling `nav('/')` updates the URL via `history.replaceState`
// but the NEW router (which only subscribes to `popstate`) never sees
// the change, so the picker stays visible until the operator refreshes
// manually. A hard reload sidesteps the swap entirely: the next boot
// reads `pc.activeOrgId` / `pc.activeStoreId` / `pc.activeMode` from
// localStorage, constructs ONE router with the correct subtree, and
// renders `/` against a fresh QueryClient (so an explicit
// `qc.invalidateQueries()` is redundant here).
function enterWorkspace(): void {
  if (typeof window === 'undefined') return;
  window.location.replace('/');
}

function formatDate(d: Date): string {
  try {
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

export function OrgStorePicker() {
  const [params] = useSearchParams();
  const { signOut } = useAuth();
  const {
    setActiveOrg,
    setActiveStore,
    setActiveOrgMode,
    ready: ctxReady,
  } = useActiveContext();

  // Capture ?ref=CODE on the choose-context route so the FIRST org the user
  // bootstraps from this page is paired with a partner. The redeem RPC
  // is best-effort -- if it fails, org creation still succeeds and the
  // /pair banner takes over.
  const referralCode = useMemo(() => {
    const raw = params.get('ref') ?? params.get('code') ?? '';
    return raw.toUpperCase().replace(/\s+/g, '') || null;
  }, [params]);

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [archivedOrgs, setArchivedOrgs] = useState<Organization[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  // The signed-in user's own membership rows (RLS-scoped to self). Used to
  // decide which stores to offer and whether to show the "Enter organization"
  // (org-wide) option at all — a store-scoped member must NOT see locations
  // they can't access, nor the whole-org entry point.
  const [myMemberships, setMyMemberships] = useState<Membership[]>([]);
  const [storesLoaded, setStoresLoaded] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Which archived org is currently being restored (disables its button
  // and shows a "Restoring…" label) — see onRestoreOrg.
  const [restoringOrgId, setRestoringOrgId] = useState<string | null>(null);
  // Tracks WHICH store row is the click target while onEnter is in flight,
  // so we can disable every Enter button (preventing the parallel-onEnter
  // races that the old implementation depended on) and only show the
  // "Entering..." label on the row the user actually clicked.
  const [enteringStoreId, setEnteringStoreId] = useState<string | null>(null);
  // Same dance, but for the "Enter organization" full-width row above
  // the store list. Mutually exclusive with `enteringStoreId` because
  // both share the same final navigate('/').
  const [enteringOrgId, setEnteringOrgId] = useState<string | null>(null);

  // Onboarding fields (shown when the user has zero orgs)
  const [orgName, setOrgName] = useState('');
  const [storeName, setStoreName] = useState('Main store');

  // Wait for ActiveContextProvider to finish hydrating before fetching
  // orgs ourselves. Otherwise we race the parent's
  // `autoAcceptPendingInvitations()` and may render a stale orgs list
  // (e.g., showing the "create your first workspace" form for a user
  // who is about to be auto-joined to an org by a pending invitation).
  useEffect(() => {
    if (!ctxReady) return;
    let alive = true;
    Promise.all([
      listMyOrganizations(),
      listMyArchivedOrganizations(),
      listMyMemberships(),
    ])
      .then(([active, archived, memberships]) => {
        if (!alive) return;
        setOrgs(active);
        setArchivedOrgs(archived);
        setMyMemberships(memberships);
        if (active.length === 1) setSelectedOrg(active[0]!.id);
      })
      .catch((e) => setError(describeError(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [ctxReady]);

  useEffect(() => {
    if (!selectedOrg) return;
    setStoresLoaded(false);
    let alive = true;
    listStoresForOrg(selectedOrg)
      .then((rows) => {
        if (!alive) return;
        setStores(rows);
      })
      .catch((e) => alive && setError(describeError(e)))
      .finally(() => alive && setStoresLoaded(true));
    return () => {
      alive = false;
    };
  }, [selectedOrg]);

  // Access scope for the currently selected org, derived from the user's own
  // (non-archived) membership rows. An org-wide membership has store_id = null
  // (owners/admins always; org-scoped operators/viewers); a store-scoped member
  // has one row per accessible store.
  const myOrgMemberships = useMemo(
    () =>
      selectedOrg
        ? myMemberships.filter((m) => m.org_id === selectedOrg && !m.archived_at)
        : [],
    [myMemberships, selectedOrg],
  );
  // Org-wide = an org-scoped membership (store_id null) OR owner/admin.
  // Mirrors the server `core.user_has_org_wide_access` gate via the shared
  // `membershipIsOrgWide` helper, so the "Enter organization" button is shown
  // to exactly the users the org-mode RPCs will accept. A store-scoped member
  // sees the org (to reach their stores) but NO org-level Enter button.
  const hasOrgWideAccess = useMemo(
    () => myOrgMemberships.some((m) => membershipIsOrgWide(m)),
    [myOrgMemberships],
  );
  const accessibleStoreIds = useMemo(() => {
    const set = new Set<string>();
    for (const m of myOrgMemberships) if (m.store_id) set.add(m.store_id);
    return set;
  }, [myOrgMemberships]);
  // Org-wide members see every store; store-scoped members only the ones they
  // were granted.
  const visibleStores = useMemo(
    () =>
      hasOrgWideAccess
        ? stores
        : stores.filter((s) => accessibleStoreIds.has(s.id)),
    [stores, hasOrgWideAccess, accessibleStoreIds],
  );

  async function onBootstrap(e: FormEvent) {
    e.preventDefault();
    if (!orgName.trim()) return;
    setError(null);
    setBusy(true);
    try {
      const { orgId, storeId } = await bootstrapOrg({
        orgName: orgName.trim(),
        orgSlug: slugify(orgName.trim()) || 'org',
        storeName: storeName.trim() || 'Main store',
        storeSlug: slugify(storeName.trim() || 'main') || 'main',
        referralCode: referralCode,
      });
      await setActiveOrg(orgId);
      setActiveStore(storeId);
      enterWorkspace();
    } catch (err) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  async function onEnter(orgId: string, storeId: string) {
    if (enteringStoreId || enteringOrgId) return;
    setEnteringStoreId(storeId);
    setError(null);
    try {
      await setActiveOrg(orgId);
      setActiveStore(storeId);
      enterWorkspace();
    } catch (e) {
      setError(describeError(e));
      setEnteringStoreId(null);
    }
    // No `finally` to clear enteringStoreId on success: we just kicked off
    // a full page load and the component is unmounting.
  }

  // Enter the org WITHOUT picking a store -- the "Enter organization"
  // mode. Mounts under `OrgAppShell` with a slim sidebar and renders the
  // combined-analytics dashboard at `/`. Operator can still drill into a
  // specific store from the org pages' deep links.
  async function onEnterOrg(orgId: string) {
    if (enteringStoreId || enteringOrgId) return;
    setEnteringOrgId(orgId);
    setError(null);
    try {
      await setActiveOrgMode(orgId);
      enterWorkspace();
    } catch (e) {
      setError(describeError(e));
      setEnteringOrgId(null);
    }
  }

  // Self-service restore of an archived org (owner/admin, inside the
  // 30-day window). Server enforces both the role and the window; on
  // success move the org from the archived list back into the active list
  // so the operator can select it immediately without a reload.
  async function onRestoreOrg(orgId: string) {
    if (restoringOrgId) return;
    setRestoringOrgId(orgId);
    setError(null);
    try {
      const restored = await unarchiveMyOrganization(orgId);
      setArchivedOrgs((prev) => prev.filter((o) => o.id !== orgId));
      setOrgs((prev) =>
        [...prev, restored].sort((a, b) => a.name.localeCompare(b.name)),
      );
    } catch (e) {
      setError(describeError(e));
    } finally {
      setRestoringOrgId(null);
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen">
        <BootLoader />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-paper px-4 py-12">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <SmallCap>PeptideClients</SmallCap>
            <Display size="md" className="mt-2">
              Choose a <em>workspace</em>
            </Display>
          </div>
          <button onClick={signOut} className="text-sm text-muted underline hover:text-ink">
            Sign out
          </button>
        </div>

        {error ? <p className="mb-4 text-sm text-clay">{error}</p> : null}

        {orgs.length === 0 ? (
          <Card title="Set up your business" hint="First-run">
            <p className="mb-6 text-sm text-ink-2">
              You don&apos;t have a workspace yet. Create your organization and its first store to
              get started.
            </p>
            <form onSubmit={onBootstrap} className="flex flex-col gap-4">
              <Field label="Organization name" htmlFor="org">
                <Input
                  id="org"
                  required
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Acme Peptides"
                />
              </Field>
              <Field label="First store" htmlFor="store" hint="You can rename or add more later.">
                <Input
                  id="store"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                />
              </Field>
              <Button type="submit" disabled={busy}>
                {busy ? 'Setting up...' : 'Create workspace'}
              </Button>
            </form>
          </Card>
        ) : (
          <Card title="Your organizations">
            <ul className="flex flex-col">
              {orgs.map((o, i) => (
                <li key={o.id}>
                  {i > 0 ? <Rule /> : null}
                  <div className="py-4 flex items-baseline justify-between gap-4">
                    <div>
                      <p className="display text-lg">{o.name}</p>
                      <p className="text-xs text-muted tabular">/{o.slug}</p>
                    </div>
                    <Button
                      variant={selectedOrg === o.id ? 'primary' : 'ghost'}
                      onClick={() => setSelectedOrg(o.id)}
                    >
                      {selectedOrg === o.id ? 'Selected' : 'Choose'}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>

            {selectedOrg && storesLoaded ? (
              hasOrgWideAccess || visibleStores.length > 0 ? (
                <>
                  <Rule className="my-4" />
                  {/* Org-mode entry row. Only shown to members with org-wide
                      access (an org-scoped membership, i.e. store_id = null —
                      owners/admins always, plus org-scoped operators/viewers).
                      A store-scoped member has no business in the
                      "view everything at once" combined-analytics view, so we
                      hide this row entirely for them. */}
                  {hasOrgWideAccess ? (
                    <div className="border border-rule bg-paper-2/40 p-3 mb-4 flex items-baseline justify-between gap-4">
                      <div>
                        <p className="text-ink">Enter organization</p>
                        <p className="text-xs text-muted">
                          Combined analytics, calendar, clients, and orders
                          across every store. Read-only; click a row to drop
                          into that store to edit.
                        </p>
                      </div>
                      <Button
                        onClick={() => onEnterOrg(selectedOrg)}
                        disabled={
                          enteringStoreId != null || enteringOrgId != null
                        }
                      >
                        {enteringOrgId === selectedOrg ? 'Entering…' : 'Enter'}
                      </Button>
                    </div>
                  ) : null}
                  <SmallCap>Stores</SmallCap>
                  <ul className="mt-2 flex flex-col">
                    {visibleStores.map((s, i) => (
                      <li key={s.id}>
                        {i > 0 ? <Rule /> : null}
                        <div className="py-3 flex items-baseline justify-between gap-4">
                          <div>
                            <p className="text-ink">{s.name}</p>
                            <p className="text-xs text-muted tabular">/{s.slug}</p>
                          </div>
                          <Button
                            onClick={() => onEnter(selectedOrg, s.id)}
                            disabled={
                              enteringStoreId != null || enteringOrgId != null
                            }
                          >
                            {enteringStoreId === s.id ? 'Entering…' : 'Enter'}
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <Rule className="my-4" />
                  <p className="text-sm text-ink-2">
                    You don&apos;t have access to any active locations in this
                    organization yet. Ask an administrator to grant you access
                    to a store.
                  </p>
                </>
              )
            ) : null}
          </Card>
        )}

        {archivedOrgs.length > 0 ? (
          <details className="mt-6">
            <summary className="cursor-pointer select-none list-none py-2 text-sm text-muted hover:text-ink">
              <SmallCap>Archived organizations ({archivedOrgs.length})</SmallCap>
            </summary>
            <Card className="mt-3">
              <p className="mb-4 text-xs text-ink-2">
                Archived workspaces are hidden from the list above. You can
                restore one yourself within {ORG_ARCHIVE_RESTORE_WINDOW_DAYS} days
                of archiving. After that, contact PeptideClients support to
                recover it.
              </p>
              <ul className="flex flex-col">
                {archivedOrgs.map((o, i) => {
                  const anonymized = Boolean(o.anonymized_at);
                  const deadline = orgRestoreDeadline(o.archived_at);
                  const expired = anonymized
                    ? true
                    : deadline
                      ? deadline.getTime() <= Date.now()
                      : false;
                  return (
                    <li key={o.id}>
                      {i > 0 ? <Rule /> : null}
                      <div className="py-4 flex items-baseline justify-between gap-4">
                        <div>
                          <p className="display text-lg text-muted">{o.name}</p>
                          <p className="text-xs text-muted tabular">/{o.slug}</p>
                          <p
                            className={
                              'mt-1 text-xs ' +
                              (expired ? 'text-clay' : 'text-ink-2')
                            }
                          >
                            {anonymized
                              ? 'Anonymized \u2014 cannot be restored'
                              : expired
                                ? 'Restore window closed \u2014 contact support to recover'
                                : deadline
                                  ? `Self-restore until ${formatDate(deadline)}`
                                  : 'Archived'}
                          </p>
                        </div>
                        {anonymized ? null : (
                          <Button
                            variant="ghost"
                            onClick={() => onRestoreOrg(o.id)}
                            disabled={expired || restoringOrgId != null}
                          >
                            {restoringOrgId === o.id ? 'Restoring\u2026' : 'Restore'}
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </details>
        ) : null}
      </div>
    </main>
  );
}
